import React, { Suspense, useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import {LoginProps} from "../types/authority";
import {checkAuthority, getBoardDetailShare, getBoardListShare} from "../api/authority";
import {useLocation, useNavigate} from "react-router-dom";
import {
  fetchMemberInfoAsync,
  setChangeGuestMode,
  setOpenDropDialogChange,
  setOpenMembershipPurchaseDialogChange,
  setOpenYearMemberDialogChange,
  setOpenWaterShortage,
  setOpenWaterShortage2
} from "../store/memberSlice";
import FallbackComponent from "./FallbackComponent";
import {setSnackbarOpen} from "../store/taskSlice";
import {useTranslation} from "react-i18next";
import {getLocalStorage} from "../util/storage";
import {IsVisitor} from "../util/author";
import {useMatomoTrackingPoint} from "../hooks/useMatomoTrackingPoint";
import {initUserConfig} from "../config/userConfig";

// 添加错误边界组件
class ErrorBoundary extends React.Component<
    { fallback: React.ReactNode; children?: React.ReactNode },
    { hasError: boolean; error?: Error }
> {
  state = { hasError: false, error: undefined };

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error) {
    console.error('Remote component error:', error);
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

const RemoteInspiration = React.lazy(() =>
    import('inspireBoard/Draw')
        .catch((error) => {
          console.error('Failed to load remote module:', error);
          window.dispatchEvent(new CustomEvent('module-federation-retry'));
          throw error;
        })
);

function RemoteInspireBoardComponent(props: {userInfo: LoginProps | null}) {
  const [retryCount, setRetryCount] = useState(0);

  const boardData = useSelector((state: RootState) => state.board);
  const memberInfo = useSelector((state: RootState) => state.member.memberInfo);
  const imagePricingRules = useSelector((state: RootState) => state.member.imagePricingRules);
  const GuestMode = useSelector((state: RootState) => state.member.GuestMode);
  const isClaimedDailyDrops = useSelector((state: RootState) => state.user.isClaimedDailyDrops);
  const chatParameter = useSelector((state: RootState) => state.user.chatParameter);
  const dispatch = useDispatch<AppDispatch>();

  const {trackWaterdropLackPopupShow} = useMatomoTrackingPoint()

  const {userInfo} = props;

  const location = useLocation();
  const navigate = useNavigate();

  const queryParams = new URLSearchParams(location.search);

  const { t } = useTranslation()

  const boardId = queryParams.get('boardId');

  const useInfoStr = getLocalStorage('userInfo');
  const localUserInfo = useInfoStr ? JSON.parse(useInfoStr) as LoginProps : null;

  useEffect(() => {
    const handler = () => setRetryCount((prev) => prev + 1);
    window.addEventListener('module-federation-retry', handler);
    return () => window.removeEventListener('module-federation-retry', handler);
  }, []);

  useEffect(() => { // 校验是不是只读模式
    dispatch(setChangeGuestMode('owner'))
    if (boardId) {
      getBoardListShare(boardId).then(item => {
        if (item) {
          const borderIdForDetail = item
          if (borderIdForDetail.id) {
            if (borderIdForDetail?.userId !== localUserInfo?.uuid) {
              dispatch(setChangeGuestMode('guest'))
            }
            (window as any)._uuid = borderIdForDetail?.userId
          }else {
            checkAuthority(boardId).then(res=>{
              if (!res) navigate('/abnormalPage?type=2');
            })
          }
        }else {
          checkAuthority(boardId).then(res=>{
            if (!res) navigate('/abnormalPage?type=2');
          })
        }
      })
    }
  }, [boardId, userInfo]);

  const stressRemoteMessage = (data: any) => {
    console.log('收到远程事件', data)
    if (data.event === 'REFRESH_MEMBER') {
      dispatch(fetchMemberInfoAsync())
    }
    if (data.event === 'REFRESH_USER_CONFIG') {
      initUserConfig(dispatch)
    }
    if (data.event === 'GLOBAL_NOTICE') {
      dispatch(setSnackbarOpen({open: true, message: data.message}))
    }
    if (data.event === 'INSUFFICIENT_FUNDS') {
     // dispatch(setSnackbarOpen({open: true, message: t('BALANCE_NOT_ENOUGH')}))
      let switch_from = 'activity_popup'
      if (isClaimedDailyDrops) {
        switch_from = 'subscription_popup'
        dispatch(setOpenWaterShortage(true))
      }else {
        dispatch(setOpenWaterShortage2(true))
      }
      trackWaterdropLackPopupShow({
        device_id: getLocalStorage('device_id') || undefined,
        page_id: window.location.href,
        board_id: boardId || undefined,
        card_id: data.card_id || undefined,
        switch_from
      })
    }
    if (data.event === 'COMMON_ERROR') {
      dispatch(setSnackbarOpen({open: true, message: t('COMMON_ERROR')}))
    }
    if (data.event === 'resolution.4k.membership_required') {
      dispatch(setSnackbarOpen({open: true, message: t('resolution.4k.membership_required')}))
    }
    if (data.event === 'OPEN_MEMBER_PAGE') {
      dispatch(setOpenMembershipPurchaseDialogChange(true))
    }
    if (data.event === 'OPEN_YEAR_MEMBER') {
      dispatch(setOpenYearMemberDialogChange(true))
    }
    if (data.event === 'OPEN_WATER_PACK_PAGE') {
      dispatch(setOpenDropDialogChange(true))
    }
  }

  const GuestModeAssembly = IsVisitor() ? 'visitor' : GuestMode

  return (
      <ErrorBoundary fallback={<FallbackComponent />}>
        <Suspense fallback={<div></div>} key={retryCount}>
          <RemoteInspiration
              theme="light"
              boardData={boardData}
              userInfo={userInfo}
              memberInfo={memberInfo}
              imagePricingRules={imagePricingRules}
              GuestMode={GuestModeAssembly}
              {...{chatParameter} as {chatParameter?: string | null}}
              onEvent={(data: unknown) => stressRemoteMessage(data)}
          />
        </Suspense>
      </ErrorBoundary>
  );
}

export default RemoteInspireBoardComponent;